import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import containerConnect from '@configuratorware/configurator-frontendgui/Framework/ComponentContainer';
import { setVisible } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import {
  setLoadCode,
  loadConfiguration,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import LoadConfiguration from '../Components/Configurator/LoadConfiguration';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { consumeLoadConfigAccessDeniedIdentifier } from '../Services/ConfiguratorService';

const ACCESS_DENIED_MESSAGE_KEYS = {
  NO_SYSTEM_ACCESS: 'accessDenied.noAccess',
  NO_MACHINE_TYPE_ACCESS: 'accessDenied.noSystemAccess',
  NO_REGION_ACCESS: 'accessDenied.noRegionAccess',
  SESSION_EXPIRED: 'accessDenied.sessionExpired',
};

export const LoadConfigurationContainer = ({
  LoadConfigurationComponent,
  close,
  setLoadCode,
  loadConfiguration,
  loadCode,
  showKdb,
  showPreCalc,
  ...props
}) => {
  const [errorMessage, setErrorMessage] = useState();
  const [kdbBlobUrl, setKdbBlobUrl] = useState('');
  const [kdbFilename, setKdbFilename] = useState('');
  const [isLoadingKdb, setIsLoadingKdb] = useState(false);
  const [preCalcBlobUrl, setPreCalcBlobUrl] = useState('');
  const [preCalcFilename, setPreCalcFilename] = useState('');
  const [isLoadingPreCalc, setIsLoadingPreCalc] = useState(false);
  const onLoadClick = useMemo(
    () => async () => {
      try {
        await loadConfiguration();
      } catch (e) {
        const accessDeniedId = consumeLoadConfigAccessDeniedIdentifier();
        if (accessDeniedId) {
          setErrorMessage(ACCESS_DENIED_MESSAGE_KEYS[accessDeniedId] ?? 'accessDenied.noAccess');
        } else {
          setErrorMessage(e.message);
        }
      }
    },
    [loadConfiguration]
  );

  const onCreateKdbClick = async () => {
    if (!loadCode) {
      setErrorMessage(t('loadConfiguration.error.emptyCode'));
      return;
    }
    setIsLoadingKdb(true);
    try {
      const result = await Services.interaction.downloadDocument(
        `${getConf('network.baseURL', '/')}configurations/kdb/${loadCode}`
      );
      setKdbBlobUrl(result.url);
      setKdbFilename(result.filename || `kdb_${loadCode}.docx`);
    } catch (e) {
      setErrorMessage('kdbDocument.createDocumentError');
    } finally {
      setIsLoadingKdb(false);
    }
  };

  const onCreatePreCalcClick = async () => {
    if (!loadCode) {
      setErrorMessage(t('loadConfiguration.error.emptyCode'));
      return;
    }
    setIsLoadingPreCalc(true);
    try {
      const result = await Services.interaction.downloadDocument(
        `${getConf('network.baseURL', '/')}configurations/pre-calc/${loadCode}`
      );
      setPreCalcBlobUrl(result.url);
      setPreCalcFilename(result.filename || `pre_calc_${loadCode}.zip`);
    } catch (e) {
      setErrorMessage('preCalcDocument.createDocumentError');
    } finally {
      setIsLoadingPreCalc(false);
    }
  };

  // reset state if the modal is reopened or the user changed the code
  useEffect(() => {
    setErrorMessage('');
    setKdbBlobUrl('');
    setKdbFilename('');
    setPreCalcBlobUrl('');
    setPreCalcFilename('');
  }, [props.show, loadCode]);

  // hide the message if loading is in progress
  useEffect(() => {
    props.workInProgress && setErrorMessage('');
  }, [props.workInProgress]);
  return (
    <LoadConfigurationComponent
      {...props}
      onClose={close}
      onCodeChange={setLoadCode}
      onLoadClick={onLoadClick}
      errorMessage={errorMessage}
      onCreateKdbClick={onCreateKdbClick}
      kdbBlobUrl={kdbBlobUrl}
      kdbFilename={kdbFilename}
      loadCode={loadCode}
      isLoadingKdb={isLoadingKdb}
      showKdb={showKdb}
      showPreCalc={showPreCalc}
      onCreatePreCalcClick={onCreatePreCalcClick}
      preCalcBlobUrl={preCalcBlobUrl}
      preCalcFilename={preCalcFilename}
      isLoadingPreCalc={isLoadingPreCalc}
    />
  );
};
LoadConfigurationContainer.propTypes = {
  LoadConfigurationComponent: PropTypes.elementType.isRequired,
  close: PropTypes.func.isRequired,
  setLoadCode: PropTypes.func.isRequired,
  loadConfiguration: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  loadCode: PropTypes.string.isRequired,
  workInProgress: PropTypes.bool.isRequired,
  showKdb: PropTypes.bool.isRequired,
  showPreCalc: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ ui, configurator, ipco }) => ({
  Component: LoadConfigurationContainer,
  LoadConfigurationComponent: LoadConfiguration,
  show: !!ui.visibilityMap.loadConfiguration,
  workInProgress: !!ui.backgroundWorkMap.loadConfiguration,
  loadCode: configurator.loadCode || '',
  showKdb: !!configurator.calculation,
  showPreCalc: !!ipco?.userFlags?.showPreCalc,
});

const mapDispatchToProps = (dispatch) => ({
  close: () => dispatch(setVisible('loadConfiguration', false)),
  setLoadCode: (code) => dispatch(setLoadCode(code)),
  loadConfiguration: () => dispatch(loadConfiguration()),
});

export default containerConnect(mapStateToProps, mapDispatchToProps);
